import { useCallback, useEffect, useState } from 'react';
import type { MenuSection } from './navigationMenu';
import {
    buildOpenSections,
    findActiveSectionKey,
    readStoredOpenSections,
    resolveSessionStorage,
    writeStoredOpenSections,
    type OpenSections,
} from './navigationState';

interface NavOpenSections {
    openSections: OpenSections;
    activeSectionKey: string | undefined;
    toggleSection: (sectionKey: string) => void;
}

/**
 * 側邊欄群組的展開狀態：預設只展開目前頁面所在的群組，
 * 換到別的群組的頁面時自動展開它，手動展開／收合則記在本次工作階段。
 */
export function useNavOpenSections(
    sections: readonly MenuSection[],
    pathname: string
): NavOpenSections {
    const activeSectionKey = findActiveSectionKey(sections, pathname);
    const [openSections, setOpenSections] = useState<OpenSections>(() =>
        buildOpenSections(
            sections,
            activeSectionKey,
            readStoredOpenSections(resolveSessionStorage())
        )
    );

    useEffect(() => {
        if (activeSectionKey === undefined) return;
        setOpenSections((previous) =>
            previous[activeSectionKey] ? previous : { ...previous, [activeSectionKey]: true }
        );
    }, [activeSectionKey]);

    useEffect(() => {
        writeStoredOpenSections(resolveSessionStorage(), openSections);
    }, [openSections]);

    const toggleSection = useCallback((sectionKey: string) => {
        setOpenSections((previous) => ({ ...previous, [sectionKey]: !previous[sectionKey] }));
    }, []);

    return { openSections, activeSectionKey, toggleSection };
}
